import { fetchAuthSession } from "aws-amplify/auth";
import { releaseInfo } from "../config/releaseInfo";

const RUM_ENABLED = process.env.REACT_APP_RUM_ENABLED === "true";
const RUM_APP_MONITOR_ID = (process.env.REACT_APP_RUM_APP_MONITOR_ID || "").trim();
const RUM_REGION = (process.env.REACT_APP_RUM_REGION || "eu-central-1").trim();
const SUCCESS_SAMPLE_INCLUDED = Math.random() < 0.1;

const KNOWN_ROUTES = new Set([
  "/",
  "/aanmelden",
  "/contact",
  "/documenten",
  "/gezondeschool",
  "/groepen",
  "/groepsouders",
  "/kalender",
  "/medezeggenschap",
  "/nieuws/open-dag-19-juni",
  "/oudervereniging",
  "/sticht",
  "/team",
  "/vacatures",
  "/verhaalroute",
  "/visie",
]);

const KNOWN_STAGES = new Set([
  "application_runtime",
  "calendar_load",
  "cms_load",
  "image_render",
  "signed_url",
  "unsupported_section",
  "vacancy_load",
  "video_render",
]);

const KNOWN_ERROR_CODES = new Set([
  "auth",
  "decode",
  "fallback",
  "network",
  "not_found",
  "render",
  "timeout",
  "unknown",
  "unsupported_type",
]);

const KNOWN_MEDIA_CATEGORIES = new Set(["asset", "document", "image", "video"]);

let rumClientPromise;
let globalListenersAdded = false;

function currentRoute() {
  if (typeof window === "undefined") return "/unknown";
  return KNOWN_ROUTES.has(window.location.pathname)
    ? window.location.pathname
    : "/unknown";
}

function boundedDuration(value) {
  const duration = Number(value);
  if (!Number.isFinite(duration) || duration < 0) return 0;
  return Math.min(Math.round(duration), 120000);
}

function classifyError(error) {
  const code = String(error?.code || error?.name || "").toLowerCase();
  if (code.includes("timeout") || code.includes("timed out")) return "timeout";
  if (code.includes("credential") || code.includes("auth")) return "auth";
  if (code.includes("notfound") || code.includes("not_found") || code === "404") {
    return "not_found";
  }
  if (code.includes("decode")) return "decode";
  if (code.includes("network") || code.includes("fetch")) return "network";
  return "unknown";
}

export function sanitizeDiagnosticData({
  stage,
  durationMs,
  outcome,
  errorCode,
  mediaCategory,
}) {
  const data = {
    route: currentRoute(),
    build: releaseInfo.commitShaShort || releaseInfo.version,
    environment: releaseInfo.environment,
    stage: KNOWN_STAGES.has(stage) ? stage : "unknown",
    duration_ms: boundedDuration(durationMs),
    outcome,
  };

  if (errorCode) {
    data.error_code = KNOWN_ERROR_CODES.has(errorCode) ? errorCode : "unknown";
  }
  if (KNOWN_MEDIA_CATEGORIES.has(mediaCategory)) {
    data.media_category = mediaCategory;
  }
  return data;
}

async function createRumClient() {
  if (!RUM_ENABLED || !RUM_APP_MONITOR_ID || typeof window === "undefined") {
    return null;
  }

  try {
    const [{ AwsRum }, { createSigningConfig }] = await Promise.all([
      import("@aws-rum/web-slim"),
      import("@aws-rum/web-core"),
    ]);
    const session = await fetchAuthSession();
    if (!session.credentials) return null;

    const client = new AwsRum(RUM_APP_MONITOR_ID, releaseInfo.version, RUM_REGION, {
      allowCookies: false,
      disableAutoPageView: true,
      enableRumClient: true,
      recordResourceUrl: false,
      sessionEventLimit: 100,
      sessionSampleRate: 1,
      signing: true,
      suppressSessionStartEvent: true,
      userIdRetentionDays: 0,
    });
    client.setSigningConfigFactory(createSigningConfig);
    client.setAwsCredentials(session.credentials);
    return client;
  } catch {
    return null;
  }
}

function getRumClient() {
  if (!rumClientPromise) rumClientPromise = createRumClient();
  return rumClientPromise;
}

function record(type, data) {
  getRumClient().then((client) => {
    if (!client) return;
    try {
      client.recordEvent(type, data);
    } catch {
      // Diagnostics must never affect the visitor experience.
    }
  });
}

export function initializeObservability() {
  getRumClient();
  if (!RUM_ENABLED || typeof window === "undefined" || globalListenersAdded) return;
  globalListenersAdded = true;
  window.addEventListener("error", (event) => {
    reportAppError({ stage: "application_runtime", error: event.error });
  });
  window.addEventListener("unhandledrejection", (event) => {
    reportAppError({ stage: "application_runtime", error: event.reason });
  });
}

export function reportAppError({ stage, error, errorCode, durationMs, mediaCategory }) {
  record(
    "windroos_application_error",
    sanitizeDiagnosticData({
      stage,
      durationMs,
      outcome: "error",
      errorCode: errorCode || classifyError(error),
      mediaCategory,
    })
  );
}

export function reportPerformance({ stage, durationMs, mediaCategory }) {
  if (!SUCCESS_SAMPLE_INCLUDED) return;
  record(
    "windroos_performance_timing",
    sanitizeDiagnosticData({ stage, durationMs, outcome: "success", mediaCategory })
  );
}
